import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';
import { Child } from '../types';
import './SOCSSetupModal.css';

interface SOCSSetupModalProps {
  isOpen: boolean;
  onClose: (completed: boolean) => void;
  children: Child[];
  suggestedEmails?: string[];
  defaultEmail?: string;
}

interface Sport {
  uuid: string;
  sport_name: string;
  sport_sid: string;
  sport_url?: string;
}

interface Team {
  uuid: string;
  name: string;  // Changed from team_name to name to match backend response
  team_tid?: string;
  team_url?: string;
  sportName?: string;
  gender?: string;
  ageGroup?: string;
  teamLevel?: string | null;
  isBareAgeGroup?: boolean;  // True if team name is exactly the age group (e.g., "U8") with no gender prefix
}

interface AgeGroup {
  ageGroup: string;
  teams: Team[];
  genders: string[];
  levels: string[];
  sports: string[];
  teamCount: number;
}

interface TeamsBySport {
  sportName: string;
  teams: Team[];
}

interface ChildSchoolMapping {
  childUuid: string;
  schoolName: string;
  selectedSportSids: string[];
  selectedTeamUuids: string[];
}

const SOCSSetupModal: React.FC<SOCSSetupModalProps> = ({ isOpen, onClose, children, suggestedEmails = [], defaultEmail = '' }) => {
  const { user } = useAuth();
  const apiService = ApiService.getInstance();
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Wizard steps: 1 = School Input, 2 = Select Teams, 3 = Preview & Sync
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: School name for each child
  const [childSchoolMappings, setChildSchoolMappings] = useState<{ [childUuid: string]: string }>({});

  // Manual URL input for schools with non-standard URLs
  const [childManualUrls, setChildManualUrls] = useState<{ [childUuid: string]: string }>({});
  const [showManualUrlInput, setShowManualUrlInput] = useState<{ [childUuid: string]: boolean }>({});

  // Search results for SOCS sites
  const [searchResults, setSearchResults] = useState<{ [childUuid: string]: any[] }>({});
  const [searching, setSearching] = useState<{ [childUuid: string]: boolean }>({});
  const [selectedSite, setSelectedSite] = useState<{ [childUuid: string]: string }>({});
  const [showSearchExpanded, setShowSearchExpanded] = useState<{ [childUuid: string]: boolean }>({});

  // Age group data after comprehensive scrape
  const [ageGroupsData, setAgeGroupsData] = useState<{ [childUuid: string]: AgeGroup[] }>({});
  const [allTeamsData, setAllTeamsData] = useState<{ [childUuid: string]: Team[] }>({});

  // Step 2: Age-based filter selections per child
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<{ [childUuid: string]: string }>({});
  const [selectedGenders, setSelectedGenders] = useState<{ [childUuid: string]: string[] }>({});
  const [selectedLevels, setSelectedLevels] = useState<{ [childUuid: string]: string[] }>({});

  // Step 3: Filtered teams (grouped by sport) with selection state
  const [filteredTeamsBySport, setFilteredTeamsBySport] = useState<{ [childUuid: string]: TeamsBySport[] }>({});
  const [selectedTeams, setSelectedTeams] = useState<{ [childUuid: string]: string[] }>({});
  // "Unsure" mode per child × sport — when on for a sport, every visible team
  // for that sport is auto-selected so the backend can pull all fixtures and
  // group them. Any manual team click within that sport disables it.
  const [unsureMode, setUnsureMode] = useState<{ [childUuid: string]: { [sportName: string]: boolean } }>({});

  // Step 4: Preview counts
  const [previewCounts, setPreviewCounts] = useState<any[]>([]);

  // Step 5: Sync results
  const [syncResults, setSyncResults] = useState<any[]>([]);

  // Collapsible section states
  const [expandedChildren, setExpandedChildren] = useState<Set<string>>(new Set());
  const [expandedSports, setExpandedSports] = useState<Set<string>>(new Set());

  // Current child being processed in the wizard
  const [currentChildIndex, setCurrentChildIndex] = useState(0);

  // Track if auto-search has been triggered for each child
  const [autoSearchTriggered, setAutoSearchTriggered] = useState<{ [childUuid: string]: boolean }>({});

  // Track previous isOpen state to detect fresh open vs resume
  const wasOpen = React.useRef(false);

  useEffect(() => {
    if (isOpen) {
      // Only reset state on FRESH open (was closed, now open)
      // Don't reset on resume from background (was open, still open)
      if (!wasOpen.current) {
        // Load existing mappings when modal opens
        loadExistingMappings();
        setStep(1);
        setError(null);
        setSyncResults([]);
        // Reset auto-search tracking - initialize all children to false
        const initialAutoSearch: { [key: string]: boolean } = {};
        children.forEach(child => {
          initialAutoSearch[child.uuid] = false;
        });
        setAutoSearchTriggered(initialAutoSearch);
      }
      wasOpen.current = true;
    } else {
      wasOpen.current = false;
    }
  }, [isOpen, children]);

  // Auto-trigger search ONLY when modal first opens with existing school data
  // Do NOT auto-search when user types - wait for them to click search button
  useEffect(() => {
    // Only trigger after loadExistingMappings completes (loading === false)
    // This runs once when modal opens, not on every keystroke
    if (isOpen && step === 1 && !loading) {
      children.forEach(async (child) => {
        const schoolName = childSchoolMappings[child.uuid]?.trim();
        // Only auto-search if:
        // 1. There's a school name from existing data (not user typing)
        // 2. We haven't already triggered auto-search for this child (explicitly false)
        // 3. There are no existing search results
        // 4. Not currently searching
        // 5. No site already selected
        if (
          schoolName &&
          autoSearchTriggered[child.uuid] === false &&
          (!searchResults[child.uuid] || searchResults[child.uuid].length === 0) &&
          !searching[child.uuid] &&
          !selectedSite[child.uuid]
        ) {
          setAutoSearchTriggered(prev => ({ ...prev, [child.uuid]: true }));
          // Trigger the search
          handleSearchSchoolSite(child.uuid);
        }
      });
    }
    // Including childSchoolMappings so this runs after school names are populated
    // The autoSearchTriggered[child.uuid] === false check prevents re-triggering on typing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step, loading, children, childSchoolMappings, autoSearchTriggered]);

  const loadExistingMappings = async () => {
    setLoading(true);
    try {
      const initialMappings: { [key: string]: string } = {};
      const initialAgeGroups: { [key: string]: string } = {};
      const initialGenders: { [key: string]: string[] } = {};
      const initialLevels: { [key: string]: string[] } = {};
      const initialSelectedTeams: { [key: string]: string[] } = {};

      // Load existing mappings for each child
      for (const child of children) {
        const response = await apiService.socsGetChildMappings(child.uuid);

        if (response.success && response.data?.mappings && response.data.mappings.length > 0) {
          const mappings = response.data.mappings;

          // Get unique school name (should be same for all mappings of this child)
          // Fallback to child's school data if backend doesn't have school name
          const schoolName = mappings[0].school_name || child.school_venue || child.school || '';
          initialMappings[child.uuid] = schoolName;

          // Get school URL if available and display it as a search result
          const schoolUrl = mappings[0].school_url;
          if (schoolUrl) {
            setChildManualUrls(prev => ({ ...prev, [child.uuid]: schoolUrl }));
            setSelectedSite(prev => ({ ...prev, [child.uuid]: schoolUrl }));
            // Create a fake search result to display the saved URL
            setSearchResults(prev => ({
              ...prev,
              [child.uuid]: [{
                url: schoolUrl,
                title: `${schoolName} SOCS Site`,
                fixturesUrl: schoolUrl
              }]
            }));
          }

          // Extract team UUIDs from all mappings
          const teamUuids = mappings.map((m: any) => m.team_uuid).filter((uuid: string) => uuid);
          initialSelectedTeams[child.uuid] = teamUuids;
          console.log(`📋 Loaded ${teamUuids.length} existing teams for ${child.name}:`, teamUuids);

          // Extract saved preferences from first mapping (all should have same preferences for a given school)
          const firstMapping = mappings[0];
          if (firstMapping.selected_age_group) {
            initialAgeGroups[child.uuid] = firstMapping.selected_age_group;
          }
          if (firstMapping.selected_genders) {
            try {
              initialGenders[child.uuid] = typeof firstMapping.selected_genders === 'string'
                ? JSON.parse(firstMapping.selected_genders)
                : firstMapping.selected_genders;
            } catch (e) {
              console.error('Error parsing selected_genders:', e);
            }
          }
          if (firstMapping.selected_levels) {
            try {
              initialLevels[child.uuid] = typeof firstMapping.selected_levels === 'string'
                ? JSON.parse(firstMapping.selected_levels)
                : firstMapping.selected_levels;
            } catch (e) {
              console.error('Error parsing selected_levels:', e);
            }
          }
        } else {
          // No existing mappings, use child's school data as default
          initialMappings[child.uuid] = child.school_venue || child.school || '';
          initialSelectedTeams[child.uuid] = [];
        }
      }

      setChildSchoolMappings(initialMappings);
      setSelectedAgeGroup(initialAgeGroups);
      setSelectedGenders(initialGenders);
      setSelectedLevels(initialLevels);
      setSelectedTeams(initialSelectedTeams);
    } catch (error) {
      console.error('Error loading existing mappings:', error);
      // Initialize with child's school data on error
      const initialMappings: { [key: string]: string } = {};
      children.forEach(child => {
        initialMappings[child.uuid] = child.school_venue || child.school || '';
      });
      setChildSchoolMappings(initialMappings);
    } finally {
      setLoading(false);
    }
  };

  // Scroll to top when step changes or modal opens
  useEffect(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [step, isOpen]);

  // Real-time team filtering when age/gender/level selections change
  useEffect(() => {
    if (step !== 2) return;

    const newFilteredTeamsBySport: { [childUuid: string]: TeamsBySport[] } = {};
    const newSelectedTeams: { [childUuid: string]: string[] } = { ...selectedTeams };

    for (const child of children) {
      const ageGroup = selectedAgeGroup[child.uuid];
      if (!ageGroup) continue;

      const genders = selectedGenders[child.uuid] || [];
      const levels = selectedLevels[child.uuid] || [];
      const allTeams = allTeamsData[child.uuid] || [];

      // Filter teams locally based on selections
      const filteredTeams = allTeams.filter(team => {
        if (team.ageGroup !== ageGroup) return false;

        const hasGenderFilter = genders.length > 0 && !genders.includes('All');
        if (hasGenderFilter) {
          if (team.isBareAgeGroup) return false;
          if (!genders.includes(team.gender || 'unknown')) return false;
        }

        // Level filtering logic. "All" shows every team.
        const showAllLevels = levels.includes('All');
        if (levels.length > 0 && !showAllLevels) {
          // Explicit selection: filter by selected levels
          if (!team.teamLevel && !levels.includes(ageGroup)) return false;
          if (team.teamLevel && !levels.includes(team.teamLevel)) return false;
        }

        return true;
      });

      // Group filtered teams by sport
      const teamsBySportMap: { [sportName: string]: Team[] } = {};
      filteredTeams.forEach(team => {
        const sportName = team.sportName || 'Unknown Sport';
        if (!teamsBySportMap[sportName]) {
          teamsBySportMap[sportName] = [];
        }
        teamsBySportMap[sportName].push(team);
      });

      const teamsBySportArray: TeamsBySport[] = Object.keys(teamsBySportMap)
        .sort()
        .map(sportName => ({
          sportName,
          teams: teamsBySportMap[sportName]
        }));

      newFilteredTeamsBySport[child.uuid] = teamsBySportArray;

      // Preserve existing team selections that still exist in the filtered list.
      // For any sport with "Unsure" turned on, force the selection to include
      // every currently-visible team in that sport (so filter tweaks reapply).
      const filteredTeamUuids = filteredTeams.map(t => t.uuid);
      const currentSelections = selectedTeams[child.uuid] || [];
      const validSelections = new Set(currentSelections.filter(uuid => filteredTeamUuids.includes(uuid)));

      const childUnsure = unsureMode[child.uuid] || {};
      for (const sportGroup of teamsBySportArray) {
        if (childUnsure[sportGroup.sportName]) {
          for (const t of sportGroup.teams) validSelections.add(t.uuid);
        }
      }
      newSelectedTeams[child.uuid] = Array.from(validSelections);
    }

    setFilteredTeamsBySport(newFilteredTeamsBySport);
    setSelectedTeams(newSelectedTeams);
  }, [step, selectedAgeGroup, selectedGenders, selectedLevels, allTeamsData, children, unsureMode]);

  const handleSchoolNameChange = (childUuid: string, schoolName: string) => {
    setChildSchoolMappings(prev => ({
      ...prev,
      [childUuid]: schoolName
    }));

    // Clear search results and selections when school name changes
    setSearchResults(prev => ({ ...prev, [childUuid]: [] }));
    setSelectedSite(prev => ({ ...prev, [childUuid]: '' }));
    setShowManualUrlInput(prev => ({ ...prev, [childUuid]: false }));
  };

  const handleManualUrlChange = (childUuid: string, manualUrl: string) => {
    setChildManualUrls(prev => ({
      ...prev,
      [childUuid]: manualUrl
    }));
  };

  const handleSearchSchoolSite = async (childUuid: string) => {
    const schoolName = childSchoolMappings[childUuid]?.trim();
    if (!schoolName) {
      setError('Please enter a school name first');
      return;
    }

    setSearching(prev => ({ ...prev, [childUuid]: true }));
    setError(null);

    try {
      const response = await apiService.socsSearchSchoolSite(schoolName);

      if (response.success && response.data?.sites) {
        // Only show verified SOCS sites
        let verifiedSites = response.data.sites.filter((site: any) => site.verified);

        // Prefer HTTPS over HTTP - remove HTTP duplicates if HTTPS exists
        const httpsUrls = verifiedSites
          .filter((site: any) => site.url.startsWith('https://'))
          .map((site: any) => site.url.replace('https://', ''));
        verifiedSites = verifiedSites.filter((site: any) => {
          if (site.url.startsWith('http://')) {
            const httpHost = site.url.replace('http://', '');
            return !httpsUrls.includes(httpHost);
          }
          return true;
        });

        setSearchResults(prev => ({ ...prev, [childUuid]: verifiedSites }));

        // If only one verified site found, auto-select it
        if (verifiedSites.length === 1) {
          setSelectedSite(prev => ({ ...prev, [childUuid]: verifiedSites[0].url }));
          setChildManualUrls(prev => ({ ...prev, [childUuid]: verifiedSites[0].url }));
        }

        // If no verified sites found, show manual input
        if (verifiedSites.length === 0) {
          setShowManualUrlInput(prev => ({ ...prev, [childUuid]: true }));
          setChildManualUrls(prev => ({ ...prev, [childUuid]: 'https://www.' }));
          setError(response.error || 'No verified SOCS websites found. Please enter the URL manually.');
        }
      } else {
        // No sites found - show manual input
        setShowManualUrlInput(prev => ({ ...prev, [childUuid]: true }));
        setChildManualUrls(prev => ({ ...prev, [childUuid]: 'https://www.' }));
        setError(response.error || 'No SOCS websites found. Please enter the URL manually.');
      }
    } catch (err: any) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search for SOCS sites');
      setShowManualUrlInput(prev => ({ ...prev, [childUuid]: true }));
      setChildManualUrls(prev => ({ ...prev, [childUuid]: 'https://www.' }));
    } finally {
      setSearching(prev => ({ ...prev, [childUuid]: false }));
    }
  };

  const handleSelectSite = (childUuid: string, siteUrl: string) => {
    setSelectedSite(prev => ({ ...prev, [childUuid]: siteUrl }));
    setChildManualUrls(prev => ({ ...prev, [childUuid]: siteUrl }));
  };

  const handleStep1Next = async () => {
    // Filter children with school names (optional - some schools may not have SOCS)
    const childrenWithSchools = children.filter(child => childSchoolMappings[child.uuid]?.trim());

    if (childrenWithSchools.length === 0) {
      setError('Please enter school name for at least one child');
      return;
    }

    // Check if at least one child has a selected school site
    const childrenWithSites = childrenWithSchools.filter(child => {
      const url = childManualUrls[child.uuid]?.trim();
      return url && url !== 'https://www.' && url !== '';
    });

    if (childrenWithSites.length === 0) {
      const continueAnyway = window.confirm(
        'No school SOCS site selected.\n\n' +
        'Please either:\n' +
        '• Click a search result to select a school\n' +
        '• Enter a manual URL\n\n' +
        'Click "OK" to go back and fix this.\n' +
        'Click "Cancel" to skip SOCS setup.'
      );
      if (continueAnyway) {
        return; // Stay on current step
      } else {
        handleClose(false); // Skip SOCS
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      // Comprehensive scrape of all teams for each child's school
      const newAgeGroupsData: { [childUuid: string]: AgeGroup[] } = {};
      const newAllTeamsData: { [childUuid: string]: Team[] } = {};

      for (const child of childrenWithSchools) {
        const schoolName = childSchoolMappings[child.uuid]?.trim();
        if (!schoolName) continue;

        const manualUrl = childManualUrls[child.uuid]?.trim() || undefined;
        const response = await apiService.socsScrapeAllTeamsByAge(schoolName, manualUrl);

        if (response.success && response.data) {
          newAgeGroupsData[child.uuid] = response.data.ageGroups || [];
          newAllTeamsData[child.uuid] = response.data.allTeams || [];

          console.log('📦 Raw API response for', schoolName, ':', response.data);
          console.log('📊 All teams data:', response.data.allTeams);
          if (response.data.allTeams && response.data.allTeams.length > 0) {
            console.log('🔍 First team structure:', response.data.allTeams[0]);
          }
        } else {
          // Check if it's a "school not found" error
          const errorMsg = response.error || '';
          if (errorMsg.includes('Failed to find SOCS site')) {
            // Show manual URL input for this child and pre-populate with https://www.
            setShowManualUrlInput(prev => ({ ...prev, [child.uuid]: true }));
            setChildManualUrls(prev => ({ ...prev, [child.uuid]: prev[child.uuid] || 'https://www.' }));
            throw new Error(
              `❌ Could not auto-detect SOCS website for "${schoolName}"\n\n` +
              `We tried:\n` +
              `• https://sport.${schoolName}.com\n` +
              `• https://sport.${schoolName}.co.uk\n\n` +
              `Please enter the manual URL for the school's SOCS website below.`
            );
          }
          throw new Error(`Failed to fetch teams for ${schoolName}: ${response.error}`);
        }
      }

      setAgeGroupsData(newAgeGroupsData);
      setAllTeamsData(newAllTeamsData);
      setStep(2);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch teams');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Handle age/gender/level filter selection
  const handleAgeGroupSelect = (childUuid: string, ageGroup: string) => {
    setSelectedAgeGroup(prev => ({ ...prev, [childUuid]: ageGroup }));
  };

  const handleGenderToggle = (childUuid: string, gender: string) => {
    setSelectedGenders(prev => {
      const currentGenders = prev[childUuid] || [];

      // If "All" is clicked, clear all other selections
      if (gender === 'All') {
        return {
          ...prev,
          [childUuid]: currentGenders.includes('All') ? [] : ['All']
        };
      }

      // If a specific gender is clicked, remove "All" if present
      const isSelected = currentGenders.includes(gender);
      let newGenders = isSelected
        ? currentGenders.filter(g => g !== gender)
        : [...currentGenders.filter(g => g !== 'All'), gender];

      return {
        ...prev,
        [childUuid]: newGenders
      };
    });
  };

  const handleLevelToggle = (childUuid: string, level: string) => {
    setSelectedLevels(prev => {
      const currentLevels = prev[childUuid] || [];

      // If "All" is clicked, clear all other selections
      if (level === 'All') {
        return {
          ...prev,
          [childUuid]: currentLevels.includes('All') ? [] : ['All']
        };
      }

      // If a specific level is clicked, remove "All" if present
      const isSelected = currentLevels.includes(level);
      let newLevels = isSelected
        ? currentLevels.filter(l => l !== level)
        : [...currentLevels.filter(l => l !== 'All'), level];

      return {
        ...prev,
        [childUuid]: newLevels
      };
    });
  };

  // Toggle "Unsure" for one sport. On → select every visible team in that sport.
  // Off → clear selections for that sport (leaves other sports untouched).
  const handleUnsureToggle = (childUuid: string, sportName: string) => {
    setUnsureMode(prev => {
      const childMap = prev[childUuid] || {};
      const turningOn = !childMap[sportName];
      const sportGroup = (filteredTeamsBySport[childUuid] || []).find(g => g.sportName === sportName);
      const sportTeamUuids = sportGroup ? sportGroup.teams.map(t => t.uuid) : [];
      setSelectedTeams(s => {
        const current = s[childUuid] || [];
        const withoutSport = current.filter(uuid => !sportTeamUuids.includes(uuid));
        return {
          ...s,
          [childUuid]: turningOn ? [...withoutSport, ...sportTeamUuids] : withoutSport
        };
      });
      return { ...prev, [childUuid]: { ...childMap, [sportName]: turningOn } };
    });
  };

  // Handle team selection/deselection
  const handleTeamToggle = (childUuid: string, teamUuid: string) => {
    // Manual click means the user is picking specific teams — exit Unsure for
    // the sport this team belongs to (other sports keep their state).
    const childUnsure = unsureMode[childUuid] || {};
    const groups = filteredTeamsBySport[childUuid] || [];
    const owningSport = groups.find(g => g.teams.some(t => t.uuid === teamUuid))?.sportName;
    if (owningSport && childUnsure[owningSport]) {
      setUnsureMode(prev => ({
        ...prev,
        [childUuid]: { ...(prev[childUuid] || {}), [owningSport]: false }
      }));
    }
    setSelectedTeams(prev => {
      const currentSelected = prev[childUuid] || [];
      const isSelected = currentSelected.includes(teamUuid);

      return {
        ...prev,
        [childUuid]: isSelected
          ? currentSelected.filter(t => t !== teamUuid)
          : [...currentSelected, teamUuid]
      };
    });
  };

  const handleStep3Next = async () => {
    // Validate at least one team selected for each child with filtered teams
    const childrenWithFilteredTeams = children.filter(child => filteredTeamsBySport[child.uuid]?.length > 0);
    const childrenWithoutTeams = childrenWithFilteredTeams.filter(child => !selectedTeams[child.uuid]?.length);
    if (childrenWithoutTeams.length > 0) {
      setError('Please select at least one team for each child');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const previews: any[] = [];

      console.log('🔍 Getting preview counts for SOCS fixtures...');

      // Setup and get preview counts for each child
      for (const child of children) {
        const childSchool = childSchoolMappings[child.uuid]?.trim();
        const childTeamUuids = selectedTeams[child.uuid] || [];

        // Skip children without schools or teams
        if (!childSchool || childTeamUuids.length === 0) {
          continue;
        }

        // Setup child with school and teams (save mappings)
        const childSchoolUrl = childManualUrls[child.uuid] || selectedSite[child.uuid] || '';
        await apiService.socsSetupChild(
          child.uuid,
          childSchool,
          childSchoolUrl,
          childTeamUuids,
          selectedAgeGroup[child.uuid],
          selectedGenders[child.uuid],
          selectedLevels[child.uuid]
        );

        // Get all teams data for this child
        const allTeams = allTeamsData[child.uuid] || [];

        // Get preview counts for every selected team in parallel — preview is
        // read-only per team (no grouping), so concurrency is safe and cuts the
        // wait from O(n × 700ms) to roughly the slowest single fetch.
        const teamsToPreview = childTeamUuids
          .map(teamUuid => allTeams.find(t => t.uuid === teamUuid))
          .filter((t): t is NonNullable<typeof t> => !!t && !!t.team_url);

        const previewResults = await Promise.all(teamsToPreview.map(team =>
          apiService.socsPreviewFixtures(
            childSchool,
            team.team_tid || '',
            team.team_url || '',
            child.uuid
          ).then(r => ({ team, response: r })).catch(err => ({ team, response: { success: false, error: err?.message } as any }))
        ));

        for (const { team, response } of previewResults) {
          if (response.success && response.data) {
            previews.push({
              childName: child.name,
              sportName: team.sportName || 'Unknown Sport',
              teamName: team.name,
              newFixturesCount: response.data.new_fixtures_count || 0
            });
          }
        }
      }

      setPreviewCounts(previews);
      setStep(3);
    } catch (err: any) {
      console.error('❌ SOCS preview error:', err);
      setError(err.message || 'Failed to get fixture preview');
    } finally {
      setLoading(false);
    }
  };

  const handleStep4Confirm = async () => {
    setLoading(true);
    setError(null);

    try {
      const results: any[] = [];

      console.log('🏁 Creating SOCS activities...');

      // Create activities for each child
      for (const child of children) {
        const childSchool = childSchoolMappings[child.uuid]?.trim();
        const childTeamUuids = selectedTeams[child.uuid] || [];

        if (!childSchool || childTeamUuids.length === 0) {
          continue;
        }

        const allTeams = allTeamsData[child.uuid] || [];

        // Build one batch payload for this child — the backend processes the
        // teams sequentially server-side and responds immediately (async).
        const teamsPayload = childTeamUuids
          .map(teamUuid => allTeams.find(t => t.uuid === teamUuid))
          .filter((t): t is NonNullable<typeof t> => !!t && !!t.team_url)
          .map(t => ({ teamTid: t.team_tid || '', teamUrl: t.team_url || '' }));

        if (teamsPayload.length === 0) continue;

        const batchResponse = await apiService.socsScrapeFixturesForChild(
          childSchool,
          child.uuid,
          teamsPayload
        );

        if (batchResponse.success) {
          results.push({
            childName: child.name,
            sportName: 'All selected',
            teamName: `${teamsPayload.length} teams`,
            activitiesCreated: batchResponse.data?.queued || teamsPayload.length
          });
        }
      }

      setSyncResults(results);
      handleClose(true);
    } catch (err: any) {
      console.error('❌ SOCS sync error:', err);
      setError(err.message || 'Failed to sync fixtures');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (completed: boolean = false) => {
    setStep(1);
    setError(null);
    setChildSchoolMappings({});
    setChildManualUrls({});
    setShowManualUrlInput({});
    setSearchResults({});
    setSearching({});
    setSelectedSite({});
    setShowSearchExpanded({});
    setAgeGroupsData({});
    setAllTeamsData({});
    setSelectedAgeGroup({});
    setSelectedGenders({});
    setSelectedLevels({});
    setFilteredTeamsBySport({});
    setSelectedTeams({});
    setUnsureMode({});
    setSyncResults([]);
    setExpandedChildren(new Set());
    setExpandedSports(new Set());
    setCurrentChildIndex(0);
    onClose(completed);
  };

  // Toggle functions for collapsible sections
  const toggleChild = (childUuid: string) => {
    const newExpanded = new Set(expandedChildren);
    if (newExpanded.has(childUuid)) {
      newExpanded.delete(childUuid);
    } else {
      newExpanded.add(childUuid);
    }
    setExpandedChildren(newExpanded);
  };

  const toggleSport = (key: string) => {
    const newExpanded = new Set(expandedSports);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedSports(newExpanded);
  };

  if (!isOpen) return null;

  return (
    <div className="socs-modal-overlay" onClick={() => handleClose(false)}>
      <div className="socs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="socs-modal-header">
          <h2>🏅 SOCS School Sports</h2>
          <button className="socs-modal-close" onClick={() => handleClose(false)}>×</button>
        </div>

        <div className="socs-modal-content" ref={modalContentRef}>
          {/* Step Indicator */}
          <div className="socs-steps">
            <div className={`socs-step ${step === 1 ? 'active' : step > 1 ? 'completed' : ''}`}>1. School</div>
            <div className={`socs-step ${step === 2 ? 'active' : step > 2 ? 'completed' : ''}`}>2. Select Teams</div>
            <div className={`socs-step ${step === 3 ? 'active' : ''}`}>3. Preview & Sync</div>
          </div>

          {error && <div className="socs-error">{error}</div>}

          {/* Step 1: School Name Input */}
          {step === 1 && (
            <div className="socs-step-content">
              {/* Only show instructions if no children have search results or selected sites */}
              {!children.some(c => (searchResults[c.uuid]?.length > 0) || selectedSite[c.uuid]) && (
                <>
                  <p>Enter the school name for each child and we'll search for their SOCS sports website.</p>
                  <p className="socs-hint">Example: For "https://sport.harrisgate.com" enter just "harrisgate"</p>
                  <p className="socs-hint" style={{ fontStyle: 'normal', color: '#888', marginTop: '8px' }}>
                    Note: Leave blank if the child's school doesn't have a SOCS account.
                  </p>
                </>
              )}

              {children.filter(c => !c.is_parent).map(child => (
                <div key={child.uuid} className="socs-child-input">
                  <label>{(child.display_name || child.name)}'s School:</label>

                  {/* Show selected school if found and not expanded */}
                  {selectedSite[child.uuid] && !showSearchExpanded[child.uuid] && !searching[child.uuid] && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#d4edda',
                      border: '2px solid #28a745',
                      borderRadius: '8px',
                      marginTop: '8px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: '#155724', marginBottom: '4px' }}>
                            ✓ School Found
                          </div>
                          <a
                            href={selectedSite[child.uuid]}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#007bff', fontSize: '13px' }}
                          >
                            {selectedSite[child.uuid]}
                          </a>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowSearchExpanded(prev => ({ ...prev, [child.uuid]: true }))}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            backgroundColor: 'transparent',
                            color: '#666',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Not Your School?
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Show searching spinner when searching */}
                  {searching[child.uuid] && (
                    <div style={{
                      marginTop: '8px',
                      padding: '16px',
                      backgroundColor: '#e3f2fd',
                      border: '1px solid #90caf9',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        border: '3px solid #90caf9',
                        borderTop: '3px solid #1976d2',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      <span style={{ color: '#1565c0', fontSize: '14px', fontWeight: 500 }}>
                        Searching for "{childSchoolMappings[child.uuid]}" SOCS site...
                      </span>
                      <style>{`
                        @keyframes spin {
                          0% { transform: rotate(0deg); }
                          100% { transform: rotate(360deg); }
                        }
                      `}</style>
                    </div>
                  )}

                  {/* Show search field if no school selected OR expanded (but not while searching) */}
                  {!searching[child.uuid] && (!selectedSite[child.uuid] || showSearchExpanded[child.uuid]) && (
                    <>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <input
                          type="text"
                          value={childSchoolMappings[child.uuid] || ''}
                          onChange={(e) => handleSchoolNameChange(child.uuid, e.target.value)}
                          placeholder="e.g. harrisgate"
                          style={{ flex: 1 }}
                        />
                        <button
                          onClick={() => handleSearchSchoolSite(child.uuid)}
                          disabled={!childSchoolMappings[child.uuid]?.trim()}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: childSchoolMappings[child.uuid]?.trim() ? 'pointer' : 'not-allowed',
                            opacity: childSchoolMappings[child.uuid]?.trim() ? 1 : 0.5,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          🔍 Search
                        </button>
                      </div>

                      {/* Show search results */}
                      {searchResults[child.uuid] && searchResults[child.uuid].length > 0 && !searching[child.uuid] && (
                        <div style={{
                          marginTop: '12px',
                          padding: '12px',
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #dee2e6',
                          borderRadius: '4px'
                        }}>
                          <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 500 }}>
                            ✅ Found {searchResults[child.uuid].length} potential SOCS {searchResults[child.uuid].length === 1 ? 'site' : 'sites'}:
                          </p>
                          {searchResults[child.uuid].map((site, index) => (
                            <div
                              key={index}
                              onClick={() => {
                                handleSelectSite(child.uuid, site.url);
                                setShowSearchExpanded(prev => ({ ...prev, [child.uuid]: false }));
                              }}
                              style={{
                                padding: '10px',
                                marginBottom: '8px',
                                backgroundColor: selectedSite[child.uuid] === site.url ? '#d4edda' : 'white',
                                border: selectedSite[child.uuid] === site.url ? '2px solid #28a745' : '1px solid #ccc',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>
                                    <a
                                      href={site.fixturesUrl || site.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        color: '#007bff',
                                        textDecoration: 'underline'
                                      }}
                                    >
                                      {site.url}
                                    </a>
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {site.title}
                                  </div>
                                </div>
                                <div style={{ marginLeft: '8px', display: 'flex', alignItems: 'center' }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelectSite(child.uuid, site.url);
                                      setShowSearchExpanded(prev => ({ ...prev, [child.uuid]: false }));
                                    }}
                                    style={{
                                      padding: '4px 12px',
                                      backgroundColor: selectedSite[child.uuid] === site.url ? '#28a745' : '#007bff',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      fontWeight: 500
                                    }}
                                  >
                                    {selectedSite[child.uuid] === site.url ? '✓ Selected' : 'Select'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                          <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                            Click the website link if you need to verify the website
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Show manual URL input if auto-detection failed */}
                  {showManualUrlInput[child.uuid] && (
                    <div style={{ marginTop: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#ff6b6b' }}>
                        Manual URL (Required):
                      </label>
                      <input
                        type="text"
                        value={childManualUrls[child.uuid] || 'https://www.'}
                        onChange={(e) => handleManualUrlChange(child.uuid, e.target.value)}
                        placeholder="e.g. https://harrisgatesports.com"
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '2px solid #ff6b6b',
                          borderRadius: '4px'
                        }}
                      />
                      <p className="socs-hint" style={{ fontSize: '12px', marginTop: '4px', color: '#ff6b6b' }}>
                        Enter the full URL of the school's SOCS website
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Step 2: Age/Gender/Level Filter Selection */}
          {step === 2 && (
            <div className="socs-step-content">
              <p>Filter teams by age group, gender, and team level:</p>

              {children.filter(child => ageGroupsData[child.uuid]?.length > 0).map(child => {
                const ageGroups = ageGroupsData[child.uuid] || [];
                const currentAgeGroup = selectedAgeGroup[child.uuid];
                const selectedAge = ageGroups.find(ag => ag.ageGroup === currentAgeGroup);

                return (
                  <div key={child.uuid} className="socs-child-section">
                    <h3>{child.name} - {childSchoolMappings[child.uuid]}</h3>

                    {/* Age Group Selection */}
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ marginBottom: '8px', fontSize: '14px' }}>Age Group: *</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px' }}>
                        {ageGroups.map(ag => (
                          <button
                            key={ag.ageGroup}
                            onClick={() => handleAgeGroupSelect(child.uuid, ag.ageGroup)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: currentAgeGroup === ag.ageGroup ? '#007bff' : '#f0f0f0',
                              color: currentAgeGroup === ag.ageGroup ? 'white' : 'black',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {ag.ageGroup} ({ag.teamCount})
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Filters panel — narrows the team list shown below. Styled
                        distinctly so it reads as "filter the list" rather than
                        as part of the team selection itself. */}
                    {selectedAge && (
                      <div style={{
                        marginBottom: '16px',
                        padding: '10px 12px',
                        backgroundColor: '#f1f3f5',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px'
                      }}>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          color: '#6c757d',
                          marginBottom: '8px'
                        }}>
                          Filter teams below
                        </div>

                        {/* Gender Filter */}
                        {selectedAge.genders.length > 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '6px' }}>
                            <span style={{ fontSize: '13px', color: '#495057', minWidth: '70px' }}>Gender:</span>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '13px' }}>
                              <input
                                type="checkbox"
                                checked={selectedGenders[child.uuid]?.includes('All') || false}
                                onChange={() => handleGenderToggle(child.uuid, 'All')}
                              />
                              All
                            </label>
                            {selectedAge.genders.map(gender => (
                              <label key={gender} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '13px' }}>
                                <input
                                  type="checkbox"
                                  checked={selectedGenders[child.uuid]?.includes(gender) || false}
                                  onChange={() => handleGenderToggle(child.uuid, gender)}
                                />
                                {gender}
                              </label>
                            ))}
                          </div>
                        )}

                        {/* Team Level Filter */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '13px', color: '#495057', minWidth: '70px' }}>Team level:</span>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '13px' }}>
                            <input
                              type="checkbox"
                              checked={selectedLevels[child.uuid]?.includes('All') || false}
                              onChange={() => handleLevelToggle(child.uuid, 'All')}
                            />
                            All
                          </label>
                          {selectedAge.levels.map(level => (
                            <label key={level} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '13px' }}>
                              <input
                                type="checkbox"
                                checked={selectedLevels[child.uuid]?.includes(level) || false}
                                onChange={() => handleLevelToggle(child.uuid, level)}
                              />
                              {level}
                            </label>
                          ))}
                          {/* Base team option (no letter suffix) */}
                          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '13px' }}>
                            <input
                              type="checkbox"
                              checked={selectedLevels[child.uuid]?.includes(selectedAge.ageGroup) || false}
                              onChange={() => handleLevelToggle(child.uuid, selectedAge.ageGroup)}
                            />
                            {selectedAge.ageGroup}
                          </label>
                        </div>
                      </div>
                    )}

                    {/* Show filtered teams inline */}
                    {selectedAge && filteredTeamsBySport[child.uuid] && (
                      <div style={{ marginTop: '16px' }}>
                        {(() => {
                          const teamsBySport = filteredTeamsBySport[child.uuid] || [];
                          const totalTeams = teamsBySport.reduce((sum, sport) => sum + sport.teams.length, 0);
                          const selectedCount = selectedTeams[child.uuid]?.length || 0;

                          if (totalTeams === 0) {
                            return (
                              <div style={{
                                padding: '12px',
                                backgroundColor: '#fff3cd',
                                border: '1px solid #ffc107',
                                borderRadius: '4px',
                                color: '#856404'
                              }}>
                                <p style={{ margin: 0, fontSize: '14px' }}>
                                  ⚠️ No teams found with your current filters. Try selecting different options above.
                                </p>
                              </div>
                            );
                          }

                          return (
                            <>
                              <h4 style={{ marginBottom: '8px', fontSize: '14px', display: 'flex', justifyContent: 'space-between' }}>
                                <span>Select Teams:</span>
                                <span style={{ fontWeight: 'normal', color: '#666' }}>
                                  {selectedCount}/{totalTeams} selected
                                </span>
                              </h4>
                              {teamsBySport.map((sportGroup, sportIndex) => {
                                const bgClass = sportIndex % 2 === 0 ? 'socs-sport-even' : 'socs-sport-odd';

                                // Group teams by gender
                                const teamsByGender: { [gender: string]: typeof sportGroup.teams } = {};
                                sportGroup.teams.forEach(team => {
                                  const gender = (team.gender || 'unknown').toLowerCase().trim();
                                  let displayGender = 'Other';
                                  if (gender === 'boy' || gender === 'boys') {
                                    displayGender = 'Boys';
                                  } else if (gender === 'girl' || gender === 'girls') {
                                    displayGender = 'Girls';
                                  } else if (gender === 'mixed') {
                                    displayGender = 'Mixed';
                                  }
                                  if (!teamsByGender[displayGender]) {
                                    teamsByGender[displayGender] = [];
                                  }
                                  teamsByGender[displayGender].push(team);
                                });

                                // Order: Boys, Girls, Mixed, Other
                                const genderOrder = ['Boys', 'Girls', 'Mixed', 'Other'];
                                const sortedGenders = genderOrder.filter(g => teamsByGender[g]?.length > 0);

                                // If all teams are "Other" (no gender info), show them flat without gender labels
                                const hasOnlyOther = sortedGenders.length === 1 && sortedGenders[0] === 'Other';

                                const sportUnsure = !!unsureMode[child.uuid]?.[sportGroup.sportName];
                                return (
                                  <div key={`${child.uuid}_${sportGroup.sportName}`}
                                       className={`socs-sport-teams ${bgClass}`}
                                       style={{ padding: '10px 12px', marginBottom: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '6px' }}>
                                      <strong style={{ fontSize: '14px' }}>{sportGroup.sportName}:</strong>
                                      <label
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '12px', color: '#495057', fontWeight: 'normal' }}
                                        title="Don't know which team for this sport? Selects every team — the backend groups same-time/location fixtures together."
                                      >
                                        <input
                                          type="checkbox"
                                          checked={sportUnsure}
                                          onChange={() => handleUnsureToggle(child.uuid, sportGroup.sportName)}
                                        />
                                        Unsure
                                      </label>
                                    </div>
                                    {hasOnlyOther ? (
                                      // No gender info - show flat list
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginLeft: '12px' }}>
                                        {sportGroup.teams.map((team, idx) => (
                                          <span key={team.uuid} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                            <label style={{
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '4px',
                                              cursor: 'pointer',
                                              fontSize: '13px'
                                            }}>
                                              <input
                                                type="checkbox"
                                                checked={selectedTeams[child.uuid]?.includes(team.uuid) || false}
                                                onChange={() => handleTeamToggle(child.uuid, team.uuid)}
                                                style={{ cursor: 'pointer' }}
                                              />
                                              <span>{team.name}</span>
                                            </label>
                                            {idx < sportGroup.teams.length - 1 && <span style={{ color: '#999', margin: '0 4px' }}>|</span>}
                                          </span>
                                        ))}
                                      </div>
                                    ) : (
                                      // Has gender info - group by gender
                                      sortedGenders.map(gender => (
                                        <div key={gender} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px', marginLeft: '12px' }}>
                                          <span style={{ fontSize: '12px', color: '#666', minWidth: '45px' }}>{gender}:</span>
                                          {teamsByGender[gender].map((team, idx) => (
                                            <span key={team.uuid} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                              <label style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                cursor: 'pointer',
                                                fontSize: '13px'
                                              }}>
                                                <input
                                                  type="checkbox"
                                                  checked={selectedTeams[child.uuid]?.includes(team.uuid) || false}
                                                  onChange={() => handleTeamToggle(child.uuid, team.uuid)}
                                                  style={{ cursor: 'pointer' }}
                                                />
                                                <span>{team.name.replace(/^(Boys?|Girls?|Mixed)-?/i, '')}</span>
                                              </label>
                                              {idx < teamsByGender[gender].length - 1 && <span style={{ color: '#999', margin: '0 4px' }}>|</span>}
                                            </span>
                                          ))}
                                        </div>
                                      ))
                                    )}
                                  </div>
                                );
                              })}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}

              {children.filter(child => ageGroupsData[child.uuid]?.length > 0).length === 0 && (
                <p>No teams found for any children. Please check the school names.</p>
              )}
            </div>
          )}

          {/* Step 3: Preview Counts */}
          {step === 3 && (
            <div className="socs-step-content">
              <h3>Preview: New Fixtures Found</h3>
              {previewCounts.length === 0 ? (
                <p>No teams to sync.</p>
              ) : (
                <>
                  <div className="socs-results">
                    {[...previewCounts]
                      .sort((a, b) => b.newFixturesCount - a.newFixturesCount)
                      .map((preview, index) => (
                      <div key={index} className="socs-result-item">
                        <strong>{preview.childName} - {preview.sportName} - {preview.teamName}</strong>
                        <p style={{ marginTop: '4px', color: preview.newFixturesCount === 0 ? '#666' : '#28a745' }}>
                          {preview.newFixturesCount === 0
                            ? '✓ No new fixtures (already up to date)'
                            : `${preview.newFixturesCount} new ${preview.newFixturesCount === 1 ? 'fixture' : 'fixtures'} ready to sync`
                          }
                        </p>
                      </div>
                    ))}
                  </div>
                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}>
                    <strong>Total new fixtures: {previewCounts.reduce((sum, p) => sum + p.newFixturesCount, 0)}</strong>
                    <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#666' }}>
                      Click "Confirm & Create Activities" to add these fixtures to your calendar.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

        </div>

        <div className="socs-modal-footer">
          {/* Back button for all steps */}
          {step <= 3 && (
            <button
              className="socs-btn socs-btn-secondary"
              onClick={step === 1 ? () => handleClose(false) : () => setStep(step - 1)}
              disabled={loading}
            >
              Back
            </button>
          )}

          {step === 1 && (
            <>
              {/* Check if at least one child has a selected school */}
              {(() => {
                const hasSelectedSchool = children.some(child =>
                  childManualUrls[child.uuid]?.trim() &&
                  childManualUrls[child.uuid].trim() !== 'https://www.'
                );
                return hasSelectedSchool ? (
                  <button
                    className="socs-btn socs-btn-primary"
                    onClick={handleStep1Next}
                    disabled={loading}
                  >
                    {loading ? 'Loading Teams...' : 'Next: Select Teams'}
                  </button>
                ) : null;
              })()}
            </>
          )}

          {step === 2 && (
            <button
              className="socs-btn socs-btn-primary"
              onClick={handleStep3Next}
              disabled={loading || !children.some(child => selectedTeams[child.uuid]?.length > 0)}
            >
              {loading ? 'Getting Preview...' : 'Preview Fixtures'}
            </button>
          )}

          {step === 3 && (
            <button
              className="socs-btn socs-btn-primary"
              onClick={handleStep4Confirm}
              disabled={loading}
            >
              {loading ? 'Creating Activities...' :
                previewCounts.reduce((sum, p) => sum + p.newFixturesCount, 0) === 0
                  ? 'Complete Setup'
                  : 'Confirm & Create Activities'}
            </button>
          )}

        </div>
      </div>
    </div>
  );
};

export default SOCSSetupModal;
